"use client";

const API_BASE_URL = (import.meta.env?.VITE_API_URL || "").replace(/\/$/, "");

const IGNORED_VALUES = new Set([
  "",
  " ",
  null,
  undefined,
  "null",
  "undefined",
  "placeholder",
  "/placeholder.png",
  "/images/social/pic-placeholder.png",
  "/icons/person.png",
]);

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value || "");

const toAbsoluteUrl = (value) => {
  if (!value) return null;
  if (isAbsoluteUrl(value)) return value;

  if (!API_BASE_URL) {
    return value.startsWith("/") ? value : `/${value}`;
  }

  if (value.startsWith("/")) {
    return `${API_BASE_URL}${value}`;
  }

  return `${API_BASE_URL}/${value}`;
};

const extractFromObject = (candidate) => {
  if (!candidate || typeof candidate !== "object") return null;
  const fields = ["url", "path", "src", "value", "image", "preview_url", "previewUrl"];
  for (const field of fields) {
    if (candidate[field]) {
      const normalized = resolveImageUrl(candidate[field]);
      if (normalized) return normalized;
    }
  }
  return null;
};

export const resolveImageUrl = (...sources) => {
  for (const source of sources) {
    if (Array.isArray(source)) {
      const nested = resolveImageUrl(...source);
      if (nested) return nested;
      continue;
    }

    if (source && typeof source === "object") {
      const nested = extractFromObject(source);
      if (nested) return nested;
      continue;
    }

    if (typeof source === "string") {
      const trimmed = source.trim();
      if (!trimmed || IGNORED_VALUES.has(trimmed)) continue;
      return toAbsoluteUrl(trimmed);
    }
  }

  return null;
};

export const withPlaceholder = (src, fallback) => {
  return resolveImageUrl(src) || fallback || "/images/social/pic-placeholder.png";
};

/**
 * Gender cache - stores gender by user ID from profile visits
 * This helps show correct placeholders for users whose profiles have been viewed
 */
const GENDER_CACHE_KEY = 'sulhafa_gender_cache';

const getGenderCache = () => {
  try {
    const cached = localStorage.getItem(GENDER_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (e) {
    return {};
  }
};

const setGenderCache = (cache) => {
  try {
    localStorage.setItem(GENDER_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // Ignore localStorage errors
  }
};

export const cacheUserGender = (userId, gender) => {
  if (!userId || !gender) return;
  const cache = getGenderCache();
  cache[String(userId)] = gender;
  setGenderCache(cache);
};

export const getCachedUserGender = (userId) => {
  if (!userId) return null;
  const cache = getGenderCache();
  return cache[String(userId)] || null;
};

/**
 * Get user placeholder image based on gender
 * @param {string|object|undefined|null} genderOrUser - User's gender string OR user object with gender property
 * @returns {string} Path to appropriate placeholder image
 */
export const getUserPlaceholderImage = (genderOrUser) => {
  let gender = null;
  let userId = null;
  
  // If it's an object, try to extract gender from common fields
  if (genderOrUser && typeof genderOrUser === 'object') {
    gender = genderOrUser.gender || genderOrUser.sex || genderOrUser.user?.gender || genderOrUser.user?.sex;
    userId = genderOrUser.id || genderOrUser.user_id || genderOrUser.user?.id || genderOrUser.user?.user_id;
  } else {
    gender = genderOrUser;
  }
  
  // Check cache if no gender found in object
  if (!gender && userId) {
    gender = getCachedUserGender(userId);
  }
  
  if (!gender) return "/icons/person.png";
  const genderLower = String(gender).toLowerCase().trim();
  if (genderLower === "female" || genderLower === "f" || genderLower === "woman" || genderLower === "girl") {
    return "/icons/Female-noimage.Jpeg";
  }
  return "/icons/person.png"; // Default to person.png for male or unknown
};


